import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';

import { ChannelSwitcher } from './channel-switcher';
import { EditWarningBanner } from './edit-warning-banner';

interface OtherChannelsDialogProps {
  channelSlug: string;
}

export function OtherChannelsDialog({ channelSlug }: OtherChannelsDialogProps) {
  const {
    campaign,
    closeForm,
    hookForm,
    onFormSubmit,
    isSubmitting,
    isFormDirty,
    isCompleted,
  } = useEditCampaignContext();
  const isReadonly = campaign?.is_readonly;
  const isTargetMetricsEditable =
    campaign?.permissions.is_target_metrics_editable;

  const { reset: resetForm, handleSubmit } = hookForm;

  useEffect(() => {
    if (campaign) {
      resetForm({
        starts_at: campaign.starts_at,
        end_at: campaign.end_at ?? null,
        budget: campaign.budget || null,
        budget_type: campaign.budget_type ?? 'lifetime',
        end_at_type: campaign.end_at_type ?? 'fixed',
        expected_cpa: campaign.expected_cpa || null,
        target_applicants: null,
        expected_conversion: campaign.expected_conversion || null,
        target_hires: null,
      });
    }
  }, [campaign, resetForm, channelSlug]);

  return (
    <Dialog open={!!channelSlug} onOpenChange={() => closeForm()}>
      <DialogContent className="min-w-[580px]">
        <DialogHeader>
          <DialogTitle>
            {isCompleted || isReadonly ? (
              <FormattedMessage defaultMessage="Campaign details" />
            ) : (
              <FormattedMessage defaultMessage="Edit Campaign" />
            )}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className="flex flex-col gap-6 p-6">
            <ChannelIcon
              channelSlug={channelSlug}
              variant="large"
              className="mx-auto"
            />
            {isReadonly && <EditWarningBanner />}
            <ChannelSwitcher channelSlug={channelSlug} />
          </div>

          <DialogFooter>
            {!isCompleted && (!isReadonly || isTargetMetricsEditable) && (
              <Button
                type="submit"
                disabled={!isFormDirty || isSubmitting}
                className="w-40"
              >
                {isSubmitting && <Loader2 className="animate-spin" />}
                <FormattedMessage defaultMessage="Update campaign" />
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
